import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  Treemap
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Info,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Brain,
  Zap,
  Activity,
  GitCompare,
  Flame,
  PiggyBank,
  Wallet,
  CreditCard,
  DollarSign,
  Calendar,
  Filter,
  BarChart2,
  Grid3X3,
  Circle,
  ChevronDown,
  Layers,
  Target,
  Eye,
  EyeOff,
  Settings,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Menu,
  X
} from 'lucide-react';

import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useBudget } from '../../contexts/BudgetContext';
import { useDateFilter } from '../../contexts/DateFilterContext';
import AdvancedAnalytics from './AdvancedAnalytics';
import { useCurrency } from '../../contexts/CurrencyContext';

// Enhanced Color Palettes
const ENHANCED_COLORS = {
  categories: [
    '#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe',
    '#43e97b', '#38f9d7', '#fa709a', '#fee140', '#a8edea', '#fed6e3',
    '#d299c2', '#fef9d7', '#ff9a9e', '#fecfef', '#ffecd2', '#fcb69f',
    '#a8e6cf', '#dcedc1', '#ffd3a5', '#fd9853', '#74b9ff', '#0984e3'
  ]
};

// Mobile-first responsive styles
const createStyles = (themeMode, isMobile = false, isTablet = false) => ({
  container: {
    minHeight: '100vh',
    background: themeMode === 'light' 
      ? 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #f0fdf4 100%)'
      : 'transparent',
    padding: isMobile ? '4px' : isTablet ? '8px' : '12px',
    position: 'relative',
    transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'auto'
  },
  backgroundElements: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  bgCircle1: {
    position: 'absolute',
    top: '10%',
    left: '5%',
    width: isMobile ? '150px' : isTablet ? '200px' : '250px',
    height: isMobile ? '150px' : isTablet ? '200px' : '250px',
    background: themeMode === 'light' 
      ? 'rgba(59, 130, 246, 0.03)'
      : 'rgba(59, 130, 246, 0.08)',
    borderRadius: '50%',
    filter: 'blur(40px)',
    animation: 'pulse 3s infinite',
    transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1), filter 0.4s ease'
  },
  bgCircle2: {
    position: 'absolute',
    bottom: '10%',
    right: '5%',
    width: isMobile ? '200px' : isTablet ? '300px' : '350px',
    height: isMobile ? '200px' : isTablet ? '300px' : '350px',
    background: themeMode === 'light' 
      ? 'rgba(147, 51, 234, 0.03)'
      : 'rgba(147, 51, 234, 0.08)',
    borderRadius: '50%',
    filter: 'blur(40px)',
    animation: 'pulse 3s infinite',
    animationDelay: '1.5s',
    transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1), filter 0.4s ease'
  },
  bgCircle3: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: isMobile ? '120px' : isTablet ? '160px' : '200px',
    height: isMobile ? '120px' : isTablet ? '160px' : '200px',
    background: themeMode === 'light' 
      ? 'rgba(34, 197, 94, 0.03)'
      : 'rgba(34, 197, 94, 0.08)',
    borderRadius: '50%',
    filter: 'blur(40px)',
    animation: 'pulse 3s infinite',
    animationDelay: '3s',
    transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1), filter 0.4s ease',
    transform: 'translateX(-50%) translateY(-50%)'
  },
  contentWrapper: {
    position: 'relative',
    zIndex: 10,
    maxWidth: '1400px',
    margin: '0 auto',
    padding: isMobile ? '4px' : isTablet ? '8px' : '12px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    width: '100%',
    boxSizing: 'border-box'
  },
  glassCard: {
    position: 'relative',
    background: themeMode === 'light' 
      ? 'rgba(255, 255, 255, 0.85)'
      : 'rgba(255, 255, 255, 0.06)',
    backdropFilter: 'blur(20px) saturate(180%)',
    border: themeMode === 'light' 
      ? '1px solid rgba(255, 255, 255, 0.9)'
      : '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: isMobile ? '12px' : isTablet ? '16px' : '20px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    boxShadow: themeMode === 'light' 
      ? '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.04)'
      : '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)'
  },
  glassCardHover: {
    transform: 'translateY(-4px)',
    boxShadow: themeMode === 'light' 
      ? '0 4px 6px rgba(0,0,0,0.07), 0 12px 32px rgba(99,102,241,0.12), 0 20px 48px rgba(0,0,0,0.06)'
      : '0 4px 6px rgba(0,0,0,0.5), 0 12px 32px rgba(99,102,241,0.2)'
  },
  cardContent: {
    position: 'relative',
    zIndex: 10,
    padding: isMobile ? '8px' : isTablet ? '12px' : '18px'
  },
  headerSection: {
    textAlign: 'center',
    marginBottom: isMobile ? '12px' : isTablet ? '20px' : '24px',
    paddingTop: isMobile ? '4px' : '0'
  },
  headerTitle: {
    fontSize: isMobile ? '20px' : isTablet ? '28px' : '32px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    marginBottom: isMobile ? '4px' : '8px',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  headerSubtitle: {
    fontSize: isMobile ? '12px' : isTablet ? '14px' : '16px',
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    marginBottom: isMobile ? '12px' : '16px',
    transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  headerControls: {
    display: 'flex',
    justifyContent: 'center',
    gap: isMobile ? '8px' : '16px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile 
      ? 'repeat(2, 1fr)'
      : isTablet 
        ? 'repeat(3, 1fr)'
        : 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: isMobile ? '6px' : isTablet ? '10px' : '16px',
    marginBottom: isMobile ? '12px' : isTablet ? '16px' : '20px'
  },
  metricCard: {
    padding: isMobile ? '8px' : isTablet ? '12px' : '16px',
    height: isMobile ? '90px' : isTablet ? '110px' : '130px',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : isTablet ? '10px' : '14px'
  },
  metricIcon: {
    width: isMobile ? '36px' : isTablet ? '42px' : '56px',
    height: isMobile ? '36px' : isTablet ? '42px' : '56px',
    borderRadius: isMobile ? '10px' : isTablet ? '12px' : '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
    flexShrink: 0
  },
  metricContent: {
    flex: 1,
    minWidth: 0
  },
  metricTitle: {
    fontSize: '11px',
    fontWeight: '600',
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    marginBottom: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    lineHeight: 1.2
  },
  metricValue: {
    fontSize: '16px',
    fontWeight: '700',
    letterSpacing: '-0.02em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    marginBottom: '2px',
    transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    lineHeight: 1.2
  },
  metricSubtitle: {
    fontSize: isMobile ? '9px' : isTablet ? '10px' : '12px',
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    lineHeight: 1.2
  },
  tabsContainer: {
    marginBottom: isMobile ? '12px' : isTablet ? '16px' : '20px'
  },
  tabsWrapper: {
    display: 'flex',
    background: themeMode === 'light' 
      ? 'rgba(255, 255, 255, 0.8)'
      : 'rgba(255, 255, 255, 0.05)',
    borderRadius: isMobile ? '10px' : isTablet ? '12px' : '16px',
    padding: isMobile ? '2px' : '4px',
    backdropFilter: 'blur(20px)',
    border: themeMode === 'light' 
      ? '1px solid rgba(255, 255, 255, 0.3)'
      : '1px solid rgba(255, 255, 255, 0.1)',
    overflow: 'hidden',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  tab: {
    flex: 1,
    padding: isMobile ? '8px 4px' : isTablet ? '12px 8px' : '16px 20px',
    borderRadius: isMobile ? '8px' : isTablet ? '10px' : '12px',
    border: 'none',
    background: 'transparent',
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: isMobile ? '10px' : isTablet ? '12px' : '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isMobile ? '2px' : isTablet ? '4px' : '8px',
    minHeight: isMobile ? '44px' : 'auto'
  },
  tabActive: {
    background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
    color: 'white',
    boxShadow: '0 0 20px rgba(99, 102, 241, 0.22), 0 0 40px rgba(99, 102, 241, 0.11)',
    transform: 'scale(1.02)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  chartContainer: {
    marginBottom: isMobile ? '12px' : isTablet ? '16px' : '20px'
  },
  chartCard: {
    padding: isMobile ? '8px' : isTablet ? '12px' : '18px',
    minHeight: isMobile ? '300px' : isTablet ? '380px' : '480px',
    maxHeight: isMobile ? '400px' : isTablet ? '500px' : '600px'
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: isMobile ? '12px' : isTablet ? '16px' : '20px',
    flexWrap: 'wrap',
    gap: isMobile ? '8px' : '12px'
  },
  chartTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '8px' : '12px',
    flex: 1,
    minWidth: 0
  },
  chartTitleIcon: {
    width: isMobile ? '28px' : isTablet ? '32px' : '40px',
    height: isMobile ? '28px' : isTablet ? '32px' : '40px',
    borderRadius: isMobile ? '8px' : isTablet ? '10px' : '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    flexShrink: 0
  },
  chartTitleText: {
    fontSize: isMobile ? '14px' : isTablet ? '16px' : '20px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    lineHeight: 1.2
  },
  chartTitleSubtext: {
    fontSize: isMobile ? '10px' : isTablet ? '12px' : '14px',
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    lineHeight: 1.2
  },
  chartContent: {
    height: isMobile ? '250px' : isTablet ? '320px' : '400px',
    width: '100%'
  },
  insightsPanel: {
    marginBottom: isMobile ? '12px' : isTablet ? '16px' : '20px'
  },
  insightsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '8px' : '12px',
    marginBottom: isMobile ? '12px' : '16px'
  },
  insightsIcon: {
    width: isMobile ? '32px' : isTablet ? '40px' : '48px',
    height: isMobile ? '32px' : isTablet ? '40px' : '48px',
    borderRadius: isMobile ? '8px' : isTablet ? '10px' : '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
    color: 'white'
  },
  insightsTitle: {
    fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc'
  },
  insightsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? '8px' : '12px'
  },
  insightItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: isMobile ? '8px' : '12px',
    padding: isMobile ? '12px' : '16px',
    borderRadius: isMobile ? '8px' : '12px',
    background: themeMode === 'light' 
      ? 'rgba(59, 130, 246, 0.05)'
      : 'rgba(59, 130, 246, 0.1)',
    border: themeMode === 'light' 
      ? '1px solid rgba(59, 130, 246, 0.1)'
      : '1px solid rgba(59, 130, 246, 0.2)'
  },
  insightIcon: {
    width: isMobile ? '24px' : '32px',
    height: isMobile ? '24px' : '32px',
    borderRadius: isMobile ? '6px' : '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  insightContent: {
    flex: 1
  },
  insightTitle: {
    fontSize: isMobile ? '12px' : isTablet ? '14px' : '15px',
    fontWeight: '600',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff',
    marginBottom: isMobile ? '2px' : '4px'
  },
  insightMessage: {
    fontSize: isMobile ? '10px' : isTablet ? '12px' : '13px',
    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    lineHeight: '1.4'
  },
  toggleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    padding: isMobile ? '8px 12px' : '8px 16px',
    background: 'transparent',
    border: themeMode === 'light' 
      ? '1px solid rgba(31, 41, 55, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: isMobile ? '6px' : '8px',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '500',
    minHeight: isMobile ? '36px' : 'auto'
  },
  mobileMenuButton: {
    display: isMobile ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    background: themeMode === 'light' 
      ? 'rgba(59, 130, 246, 0.1)'
      : 'rgba(255, 255, 255, 0.1)',
    border: themeMode === 'light' 
      ? '1px solid rgba(59, 130, 246, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: themeMode === 'light' 
      ? 'rgba(255, 255, 255, 0.98)'
      : 'rgba(0, 0, 0, 0.98)',
    backdropFilter: 'blur(20px)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    transition: 'all 0.3s ease'
  },
  mobileMenuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: themeMode === 'light' 
      ? '1px solid rgba(31, 41, 55, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.1)'
  },
  mobileMenuTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff'
  },
  mobileMenuClose: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: themeMode === 'light' 
      ? 'rgba(31, 41, 55, 0.1)'
      : 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff'
  },
  noDataCard: {
    textAlign: 'center',
    padding: isMobile ? '24px 16px' : '40px 20px'
  },
  noDataIcon: {
    width: isMobile ? '60px' : '80px',
    height: isMobile ? '60px' : '80px',
    borderRadius: isMobile ? '16px' : '20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: isMobile ? '0 auto 16px' : '0 auto 20px'
  },
  noDataTitle: {
    fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
    fontWeight: '700',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff',
    marginBottom: isMobile ? '6px' : '8px',
    transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  noDataMessage: {
    fontSize: isMobile ? '12px' : isTablet ? '14px' : '16px',
    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    marginBottom: isMobile ? '16px' : '20px',
    transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    lineHeight: 1.4
  },
  getStartedButton: {
    padding: isMobile ? '10px 20px' : '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: isMobile ? '8px' : '12px',
    color: 'white',
    fontSize: isMobile ? '14px' : '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    margin: '0 auto',
    minHeight: isMobile ? '40px' : 'auto'
  },
  advancedSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: isMobile ? '200px' : '300px',
    textAlign: 'center',
    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)'
  },
  timeRangeControl: {
    display: isMobile ? 'none' : 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  timeRangeLabel: {
    fontSize: '14px',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff'
  },
  timeRangeSlider: {
    width: '100px',
    height: '4px',
    background: themeMode === 'light' 
      ? 'rgba(31, 41, 55, 0.2)'
      : 'rgba(255, 255, 255, 0.2)',
    borderRadius: '2px',
    outline: 'none',
    cursor: 'pointer'
  }
});

// Modern Floating Card Component with mobile optimization
const ModernFloatingCard = ({ 
  children, 
  style = {}, 
  glowColor = 'rgba(102, 126, 234, 0.3)',
  hover3D = true,
  onClick,
  themeMode,
  isMobile
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);
  const styles = createStyles(themeMode, isMobile);

  const handleMouseMove = (e) => {
    if (!cardRef.current || !hover3D || isMobile) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    
    setMousePosition({ x, y });
  };

  const handleTouch = () => {
    if (isMobile) {
      setIsHovered(!isHovered);
    }
  };

  const cardStyle = {
    ...styles.glassCard,
    ...style,
    ...(isHovered && styles.glassCardHover),
    transform: hover3D && isHovered && !isMobile
      ? `perspective(1000px) rotateY(${mousePosition.x * 0.2}deg) rotateX(${-mousePosition.y * 0.2}deg) translateZ(8px)`
      : 'perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)'
  };

  return (
    <div
      ref={cardRef}
      style={cardStyle}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouch}
      onClick={onClick}
    >
      <div style={{
        position: 'absolute',
        top: '-2px',
        left: '-2px',
        right: '-2px',
        bottom: '-2px',
        borderRadius: isMobile ? '12px' : '20px',
        background: `radial-gradient(circle at 50% 50%, ${glowColor}, transparent 70%)`,
        opacity: isHovered ? 0.6 : 0,
        transition: 'opacity 0.5s ease',
        zIndex: -1
      }} />
      <div style={styles.cardContent}>
        {children}
      </div>
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ value, prefix = '', suffix = '', duration = 2000, isMobile }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(value * easeOutQuart));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);
  
  return (
    <span style={{ fontSize: 'inherit' }}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
};

// AI-Powered Insights Generator
const generateFinancialInsights = (categoryData, timeSeriesData, summaryMetrics, t) => {
  const insights = [];
  
  if (categoryData.length > 0) {
    const topCategory = categoryData[0];
    const overBudgetCategories = categoryData.filter(cat => cat.budgetUtilization > 100);
    
    if (topCategory.percentage > 40) {
      insights.push({
        type: 'warning',
        icon: Flame,
        title: t('dashboard.charts.highConcentrationRisk'),
        message: `${topCategory.percentage.toFixed(1)}% of spending is in ${topCategory.name}. Consider diversifying.`,
        severity: 'warning',
        color: '#f59e0b'
      });
    }
    
    if (overBudgetCategories.length > 0) {
      insights.push({
        type: 'error',
        icon: AlertTriangle,
        title: t('dashboard.insights.budgetOverruns'),
        message: t('dashboard.insights.overBudgetMessage', { count: overBudgetCategories.length }),
        severity: 'error',
        color: '#ef4444',
        categories: overBudgetCategories
      });
    }
  }
  
  if (timeSeriesData.length >= 3) {
    const recent = timeSeriesData.slice(-3);
    const avgSavings = recent.reduce((sum, month) => sum + month.savings, 0) / 3;
    
    if (avgSavings < 0) {
      insights.push({
        type: 'critical',
        icon: TrendingDown,
        title: t('dashboard.insights.negativeSavingsTrend'),
        message: t('dashboard.insights.negativeSavingsMessage'),
        severity: 'error',
        color: '#ef4444'
      });
    } else if (timeSeriesData.length >= 4 && avgSavings > timeSeriesData[timeSeriesData.length - 4]?.savings * 1.2) {
      insights.push({
        type: 'positive',
        icon: TrendingUp,
        title: t('dashboard.insights.improvingFinancialHealth'),
        message: t('dashboard.insights.savingsImprovedMessage', { 
          percentage: ((avgSavings / (timeSeriesData[timeSeriesData.length - 4]?.savings || 1) - 1) * 100).toFixed(1)
        }),
        severity: 'success',
        color: '#22c55e'
      });
    }
  }
  
  return insights;
};

// CSS Animations
const cssAnimations = `
  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideInFromRight {
    from { opacity: 0; transform: translateX(100%); }
    to { opacity: 1; transform: translateX(0); }
  }
  
  .mobile-menu-enter {
    animation: slideInFromRight 0.3s ease-out;
  }
  
  /* Smooth theme transitions */
  * {
    transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                border-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                fill 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                stroke 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  .theme-transition {
    transition: background 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  .glow-transition {
    transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                filter 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  /* Ensure proper scrolling and visibility */
  body {
    overflow-x: hidden;
    overflow-y: auto;
  }
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    .recharts-legend-wrapper {
      font-size: 11px !important;
    }
    
    .recharts-tooltip-wrapper {
      font-size: 12px !important;
    }
    
    .recharts-cartesian-axis-tick text {
      font-size: 10px !important;
    }
    
    body {
      padding: 0;
      margin: 0;
    }
    
    /* Ensure all content is visible */
    .mobile-container {
      max-width: 100vw;
      overflow-x: hidden;
    }
  }
`;

// Main Enhanced Dashboard Charts Component
export const DashboardCharts = ({ 
  onAddTransaction,
  onSetupBudget,
  currency = 'EUR'
}) => {
  const { formatAmount, getCurrencySymbol } = useCurrency();
  const currencySymbol = getCurrencySymbol();
  const { t } = useTranslation();
  const { themeMode } = useThemeContext();
  
  // Real user data from contexts
  const { transactions = [], categories = [] } = useBudget();
  const { selectedMonth, selectedYear, currentPeriodDisplay, filterTransactionsByMonth, filterMode, effectiveStartDate, effectiveEndDate } = useDateFilter();
  
  // Component state
  const [activeTab, setActiveTab] = useState(0);
  const [showAdvancedMetrics, setShowAdvancedMetrics] = useState(false);
  const [timeRange, setTimeRange] = useState(12);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Responsive breakpoints
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  const styles = createStyles(themeMode, isMobile, isTablet);
  const isDark = themeMode !== 'light';
  
  // Filter transactions by selected date range (supports both month and range modes)
  const currentMonthTransactions = useMemo(() => {
    return filterTransactionsByMonth(transactions)
      .filter(t => t.type === 'expense' || !t.type);
  }, [transactions, filterTransactionsByMonth]);
  
  // All filtered transactions including income (for metrics calculations)
  const allFilteredTransactions = useMemo(() => {
    return filterTransactionsByMonth(transactions);
  }, [transactions, filterTransactionsByMonth]);
  
  // Process category data
  const categoryData = useMemo(() => {
    if (!currentMonthTransactions.length || !categories.length) return [];
    
    const currentMonthKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    const categorySpending = {};
    
    currentMonthTransactions.forEach(transaction => {
      // Use enhanced categoryName first (set by BudgetContext), fall back to raw fields
      const categoryName = transaction.categoryName;
      const categoryId = transaction.categoryId || transaction.category;
      const amount = Number(transaction.amount) || 0;
      
      // Find matching category by name first (most reliable), then by ID
      const category = categoryName
        ? categories.find(cat => cat.name === categoryName)
        : categories.find(cat => 
            cat.id === categoryId || 
            String(cat.id) === String(categoryId) ||
            cat.name === categoryId
          );
      
      const lookupKey = category?.id || categoryId || categoryName || 'uncategorized';
      
      if (categorySpending[lookupKey]) {
        categorySpending[lookupKey].value += amount;
        categorySpending[lookupKey].transactionCount += 1;
      } else {
        const monthBudget = category?.monthlyBudgets?.[currentMonthKey] ?? category?.budget ?? 0;
        categorySpending[lookupKey] = {
          name: category?.name || categoryName || t('dashboard.charts.unknownCategory'),
          value: amount,
          transactionCount: 1,
          color: category?.color || ENHANCED_COLORS.categories[Object.keys(categorySpending).length % ENHANCED_COLORS.categories.length],
          icon: category?.icon || '📁',
          budget: Number(monthBudget)
        };
      }
    });
    
    const total = Object.values(categorySpending).reduce((sum, cat) => sum + cat.value, 0);
    
    return Object.values(categorySpending).map(cat => ({
      ...cat,
      total,
      percentage: total > 0 ? (cat.value / total) * 100 : 0,
      budgetUtilization: cat.budget > 0 ? (cat.value / cat.budget) * 100 : 0,
      remaining: Math.max(0, cat.budget - cat.value)
    })).sort((a, b) => b.value - a.value);
  }, [currentMonthTransactions, categories, selectedMonth, selectedYear, t]);
  
  // Generate time series data
  const timeSeriesData = useMemo(() => {
    if (!transactions.length) return [];
    
    const monthlyData = {};
    
    transactions.forEach(transaction => {
      if (!transaction.date) return;
      
      const date = new Date(transaction.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthName,
          income: 0,
          expenses: 0
        };
      }
      
      const amount = Number(transaction.amount) || 0;
      if (transaction.type === 'income') {
        monthlyData[monthKey].income += amount;
      } else {
        monthlyData[monthKey].expenses += amount;
      }
    });
    
    return Object.keys(monthlyData)
      .sort()
      .slice(-timeRange)
      .map(key => ({
        ...monthlyData[key],
        savings: monthlyData[key].income - monthlyData[key].expenses,
        savingsRate: monthlyData[key].income > 0 ? ((monthlyData[key].income - monthlyData[key].expenses) / monthlyData[key].income) * 100 : 0
      }));
  }, [transactions, timeRange]);
  
  // Calculate enhanced metrics
  const enhancedMetrics = useMemo(() => {
    const totalExpenses = categoryData.reduce((sum, cat) => sum + cat.value, 0);
    const totalBudget = categoryData.reduce((sum, cat) => sum + cat.budget, 0);
    const totalIncome = allFilteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    
    const averageMonthlyExpenses = timeSeriesData.length > 0 
      ? timeSeriesData.reduce((sum, month) => sum + month.expenses, 0) / timeSeriesData.length 
      : 0;
    
    const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0;
    
    return {
      totalExpenses,
      totalBudget,
      totalIncome,
      savingsRate,
      averageMonthlyExpenses,
      budgetUtilization: totalBudget > 0 ? (totalExpenses / totalBudget) * 100 : 0,
      transactionCount: allFilteredTransactions.length,
      hasData: allFilteredTransactions.length > 0,
      netWorth: totalIncome - totalExpenses,
      burnRate: averageMonthlyExpenses,
      runwayMonths: averageMonthlyExpenses > 0 ? (totalIncome - totalExpenses) / averageMonthlyExpenses : 0
    };
  }, [categoryData, allFilteredTransactions, timeSeriesData]);
  
  // Generate AI insights
  const aiInsights = useMemo(() => {
    return generateFinancialInsights(categoryData, timeSeriesData, enhancedMetrics, t);
  }, [categoryData, timeSeriesData, enhancedMetrics, t]);
  
  // Build real historical category spending data
  const categoryTrendData = useMemo(() => {
    const catMonthly = {};
    transactions.forEach(tx => {
      if (!tx.date || tx.type === 'income') return;
      const date = new Date(tx.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleString('default', { month: 'short' });
      const catId = tx.categoryId || tx.category;
      const cat = categories.find(c => c.id === catId || String(c._id) === String(catId));
      const catName = cat?.name || t('dashboard.charts.other');
      
      if (!catMonthly[monthKey]) catMonthly[monthKey] = { month: monthLabel, monthKey };
      catMonthly[monthKey][catName] = (catMonthly[monthKey][catName] || 0) + (Number(tx.amount) || 0);
    });
    return Object.keys(catMonthly).sort().slice(-6).map(k => catMonthly[k]);
  }, [transactions, categories]);
  
  // Calculate category month-over-month changes (for the top 5 categories)
  const categoryChanges = useMemo(() => {
    if (categoryTrendData.length < 2 || categoryData.length === 0) return [];
    
    const lastMonth = categoryTrendData[categoryTrendData.length - 1];
    const prevMonth = categoryTrendData[categoryTrendData.length - 2];
    
    return categoryData.slice(0, 5).map(cat => {
      const lastValue = lastMonth[cat.name] || 0;
      const prevValue = prevMonth[cat.name] || 0;
      const change = prevValue > 0 ? ((lastValue - prevValue) / prevValue) * 100 : 0;
      
      return {
        name: cat.name,
        change,
        direction: change > 5 ? 'up' : change < -5 ? 'down' : 'flat'
      };
    });
  }, [categoryTrendData, categoryData]);

  // Smart dynamic alerts for Overview tab
  const smartAlerts = useMemo(() => {
    const alerts = [];

    // Budget status
    if (enhancedMetrics.budgetUtilization > 100) {
      alerts.push({ emoji: '🔴', text: `Budget exceeded by ${(enhancedMetrics.budgetUtilization - 100).toFixed(0)}%`, color: '#ef4444' });
    } else if (enhancedMetrics.budgetUtilization > 85) {
      alerts.push({ emoji: '⚠️', text: `${enhancedMetrics.budgetUtilization.toFixed(0)}% of budget used`, color: '#f59e0b' });
    } else {
      alerts.push({ emoji: '✅', text: `Budget on track (${enhancedMetrics.budgetUtilization.toFixed(0)}%)`, color: '#10b981' });
    }

    // Savings rate
    if (enhancedMetrics.savingsRate >= 20) {
      alerts.push({ emoji: '🏆', text: `Saving ${enhancedMetrics.savingsRate.toFixed(0)}% — excellent!`, color: '#10b981' });
    } else if (enhancedMetrics.savingsRate >= 0) {
      alerts.push({ emoji: '💡', text: `Saving ${enhancedMetrics.savingsRate.toFixed(0)}% of income`, color: '#3b82f6' });
    } else {
      alerts.push({ emoji: '📉', text: 'Spending exceeds income', color: '#ef4444' });
    }

    // Top category insight
    if (categoryData.length > 0) {
      const top = categoryData[0];
      alerts.push({ emoji: '📊', text: `${top.name}: ${top.percentage.toFixed(0)}% of spend`, color: '#8b5cf6' });
    }

    // Daily pace vs expected
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const dayOfMonth = new Date().getDate();
    const expectedPct = (dayOfMonth / daysInMonth) * 100;
    const actualPct = enhancedMetrics.budgetUtilization;
    if (actualPct > expectedPct + 15) {
      alerts.push({ emoji: '⚡', text: 'Spending pace above average', color: '#f59e0b' });
    } else if (actualPct < expectedPct - 10 && enhancedMetrics.totalBudget > 0) {
      alerts.push({ emoji: '🎯', text: 'Spending pace below plan — great!', color: '#10b981' });
    }

    return alerts;
  }, [enhancedMetrics, categoryData, selectedYear, selectedMonth]);

  // Top movers vs last month
  const topMovers = useMemo(() => {
    if (timeSeriesData.length < 2) return [];
    const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
    const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
    const prevStart = new Date(prevYear, prevMonth, 1);
    const prevEnd = new Date(prevYear, prevMonth + 1, 0, 23, 59, 59);

    const prevCatTotals = {};
    const currCatTotals = {};

    transactions.forEach(tx => {
      if (tx.type !== 'expense' && tx.type !== 'Expense') return;
      const d = new Date(tx.date);
      const catName = tx.categoryName || tx.category || 'Other';
      const amount = Math.abs(tx.amount || 0);
      if (d >= prevStart && d <= prevEnd) {
        prevCatTotals[catName] = (prevCatTotals[catName] || 0) + amount;
      }
    });

    categoryData.forEach(cat => {
      currCatTotals[cat.name] = cat.value;
    });

    const allCats = new Set([...Object.keys(prevCatTotals), ...Object.keys(currCatTotals)]);
    const movers = [];
    allCats.forEach(cat => {
      const prev = prevCatTotals[cat] || 0;
      const curr = currCatTotals[cat] || 0;
      const diff = curr - prev;
      const pctChange = prev > 0 ? (diff / prev) * 100 : (curr > 0 ? 100 : 0);
      if (Math.abs(diff) > 5) {
        movers.push({ name: cat, diff, pctChange, curr, prev });
      }
    });

    movers.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
    return movers.slice(0, 4);
  }, [transactions, categoryData, selectedMonth, selectedYear, timeSeriesData]);

  const tabContent = [
    { label: t('dashboard.tabs.overview'), icon: Grid3X3 },
    { label: t('dashboard.tabs.categories'), icon: Circle },
    { label: t('dashboard.tabs.trends'), icon: Activity },
    { label: t('dashboard.tabs.advanced'), icon: BarChart3 }
  ];
  
  // Custom tooltip style for charts
  const chartTooltipStyle = {
    backgroundColor: themeMode === 'light' ? 'rgba(255, 255, 255, 0.98)' : 'rgba(0, 0, 0, 0.98)',
    border: `2px solid ${themeMode === 'light' ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.5)'}`,
    borderRadius: isMobile ? '8px' : '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    fontSize: isMobile ? '11px' : isTablet ? '12px' : '15px',
    padding: isMobile ? '8px' : '12px',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff'
  };
  
  if (!enhancedMetrics.hasData) {
    return (
      <div 
        style={styles.container} 
        className={`theme-transition ${isMobile ? 'mobile-container' : ''}`}
      >
        <style>{cssAnimations}</style>
        
        <div style={styles.backgroundElements} className="glow-transition">
          <div style={styles.bgCircle1} className="glow-transition" />
          <div style={styles.bgCircle2} className="glow-transition" />
          <div style={styles.bgCircle3} className="glow-transition" />
        </div>
        
        <div style={styles.contentWrapper}>
          <ModernFloatingCard style={styles.noDataCard} themeMode={themeMode} isMobile={isMobile}>
            <div style={styles.noDataIcon}>
              <BarChart2 size={isMobile ? 32 : 40} />
            </div>
            <h2 style={styles.noDataTitle}>{t('dashboard.enhanced.welcomeTitle')}</h2>
            <p style={styles.noDataMessage}>
              {t('dashboard.enhanced.welcomeMessage')}
            </p>
            <button 
              style={styles.getStartedButton}
              onClick={onAddTransaction}
              onMouseEnter={e => !isMobile && Object.assign(e.target.style, { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(102, 126, 234, 0.6)' })}
              onMouseLeave={e => !isMobile && Object.assign(e.target.style, styles.getStartedButton)}
            >
              <Sparkles size={isMobile ? 16 : 20} />
              {t('dashboard.enhanced.getStarted')}
            </button>
          </ModernFloatingCard>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      style={styles.container} 
      className={`theme-transition ${isMobile ? 'mobile-container' : ''}`}
    >
      <style>{cssAnimations}</style>
      
      <div style={styles.backgroundElements} className="glow-transition">
        <div style={styles.bgCircle1} className="glow-transition" />
        <div style={styles.bgCircle2} className="glow-transition" />
        <div style={styles.bgCircle3} className="glow-transition" />
      </div>
      
      <div style={styles.contentWrapper}>
        
        {/* Navigation Tabs */}
        <div style={styles.tabsContainer}>
          <div style={styles.tabsWrapper}>
            {tabContent.map((tab, index) => (
              <button
                key={index}
                style={{
                  ...styles.tab,
                  ...(activeTab === index && styles.tabActive)
                }}
                onClick={() => setActiveTab(index)}
              >
                <tab.icon size={isMobile ? 14 : isTablet ? 16 : 20} />
                <span>{isMobile && tab.label === t('dashboard.tabs.categories') ? t('dashboard.tabs.categoriesShort') : tab.label}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === 0 && (
          <div style={styles.chartContainer}>
            {/* ── 1-3. UNIFIED MONEY FLOW + PACE + ALERTS CARD ────── */}
            {(() => {
              /* Pace calculations (previously in section 3) */
              const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
              const today = new Date();
              const dayOfMonth = (filterMode === 'month' && today.getFullYear() === selectedYear && today.getMonth() === selectedMonth)
                ? today.getDate()
                : daysInMonth;
              const daysRemaining = Math.max(0, daysInMonth - dayOfMonth);
              const expectedSpend = enhancedMetrics.totalBudget > 0
                ? (dayOfMonth / daysInMonth) * enhancedMetrics.totalBudget
                : 0;
              const actualSpend = enhancedMetrics.totalExpenses;
              const budgetWidth = enhancedMetrics.totalBudget > 0
                ? Math.min((expectedSpend / enhancedMetrics.totalBudget) * 100, 100)
                : 0;
              const actualWidth = enhancedMetrics.totalBudget > 0
                ? Math.min((actualSpend / enhancedMetrics.totalBudget) * 100, 100)
                : 0;
              const paceDiff = expectedSpend - actualSpend;
              const isUnder = paceDiff >= 0;
              const labelColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';
              const textColor = isDark ? '#f8fafc' : '#0f172a';
              const dividerStyle = {
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                margin: isMobile ? '12px 0' : '14px 0'
              };

              /* Spend-bar color for section 1 */
              const spendPct = enhancedMetrics.totalIncome > 0
                ? Math.min((enhancedMetrics.totalExpenses / enhancedMetrics.totalIncome) * 100, 100)
                : 0;
              const spendBarColor = spendPct < 70 ? '#10b981' : spendPct < 90 ? '#f59e0b' : '#ef4444';

              return (
                <ModernFloatingCard
                  style={{ ...styles.chartCard, marginBottom: isMobile ? '12px' : '16px', padding: isMobile ? '16px' : '20px' }}
                  themeMode={themeMode}
                  isMobile={isMobile}
                  glowColor="rgba(16, 185, 129, 0.25)"
                >
                  {/* ── Section A: 3-column money summary ── */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: isMobile ? '8px' : '16px'
                  }}>
                    {/* Income */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: isMobile ? '9px' : '11px',
                        fontWeight: '600',
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        marginBottom: '4px'
                      }}>Income</div>
                      <div style={{ fontSize: isMobile ? '18px' : '26px', fontWeight: '800', color: '#10b981', lineHeight: 1.1 }}>
                        {formatAmount(enhancedMetrics.totalIncome, false, isMobile, false)}
                      </div>
                      <div style={{ fontSize: isMobile ? '9px' : '11px', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: '2px' }}>
                        earned
                      </div>
                    </div>

                    {/* Expenses */}
                    <div style={{
                      textAlign: 'center',
                      borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                      borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`
                    }}>
                      <div style={{
                        fontSize: isMobile ? '9px' : '11px',
                        fontWeight: '600',
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        marginBottom: '4px'
                      }}>Expenses</div>
                      <div style={{ fontSize: isMobile ? '18px' : '26px', fontWeight: '800', color: '#ef4444', lineHeight: 1.1 }}>
                        {formatAmount(enhancedMetrics.totalExpenses, false, isMobile, false)}
                      </div>
                      <div style={{ fontSize: isMobile ? '9px' : '11px', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: '2px' }}>
                        spent
                      </div>
                    </div>

                    {/* Net */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: isMobile ? '9px' : '11px',
                        fontWeight: '600',
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        marginBottom: '4px'
                      }}>Net</div>
                      <div style={{ fontSize: isMobile ? '18px' : '26px', fontWeight: '800', color: enhancedMetrics.netWorth >= 0 ? '#10b981' : '#ef4444', lineHeight: 1.1 }}>
                        {enhancedMetrics.netWorth >= 0 ? '+' : ''}{formatAmount(enhancedMetrics.netWorth, false, isMobile, false)}
                      </div>
                      <div style={{ fontSize: isMobile ? '9px' : '11px', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: '2px' }}>
                        {enhancedMetrics.netWorth >= 0 ? 'surplus' : 'deficit'}
                      </div>
                    </div>
                  </div>

                  {/* ── Divider + Section B: Spending progress bar ── */}
                  {enhancedMetrics.totalIncome > 0 && (
                    <>
                      <div style={dividerStyle} />
                      <div>
                        <div style={{
                          width: '100%',
                          height: isMobile ? '8px' : '10px',
                          background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
                          borderRadius: '999px',
                          overflow: 'hidden',
                          marginBottom: '6px'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${spendPct}%`,
                            background: `linear-gradient(90deg, #10b981, ${spendBarColor})`,
                            borderRadius: '999px',
                            transition: 'width 0.6s ease'
                          }} />
                        </div>
                        <div style={{ fontSize: isMobile ? '10px' : '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textAlign: 'center' }}>
                          <span style={{ color: spendBarColor, fontWeight: '600' }}>{spendPct.toFixed(0)}%</span> of income spent
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── Divider + Section C: Spending Pace Tracker ── */}
                  <div style={dividerStyle} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? '10px' : '12px' }}>
                    <div>
                      <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: textColor, marginBottom: '2px' }}>
                        ⏱ Spending Pace
                      </div>
                      <div style={{ fontSize: isMobile ? '10px' : '12px', color: labelColor }}>
                        Day {dayOfMonth} of {daysInMonth} · {daysRemaining} days left
                      </div>
                    </div>
                    {enhancedMetrics.totalBudget > 0 && (
                      <div style={{
                        padding: isMobile ? '4px 8px' : '5px 10px',
                        borderRadius: '8px',
                        background: isUnder ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: '700',
                        color: isUnder ? '#10b981' : '#ef4444'
                      }}>
                        {isUnder ? '▼' : '▲'} {isUnder ? 'Under' : 'Over'} pace
                      </div>
                    )}
                  </div>

                  {enhancedMetrics.totalBudget === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: isMobile ? '8px' : '12px',
                      color: labelColor,
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      Set budgets to track your spending pace
                    </div>
                  ) : (
                    <>
                      {/* Expected bar */}
                      <div style={{ marginBottom: isMobile ? '8px' : '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: isMobile ? '10px' : '11px', color: labelColor }}>
                          <span>Expected by today</span>
                          <span>{formatAmount(expectedSpend, false, isMobile, false)}</span>
                        </div>
                        <div style={{ width: '100%', height: isMobile ? '7px' : '8px', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)', borderRadius: '999px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${budgetWidth}%`,
                            background: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.15)',
                            borderRadius: '999px',
                            backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 6px, rgba(255,255,255,0.15) 6px, rgba(255,255,255,0.15) 12px)',
                            transition: 'width 0.6s ease'
                          }} />
                        </div>
                      </div>

                      {/* Actual bar */}
                      <div style={{ marginBottom: isMobile ? '10px' : '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: isMobile ? '10px' : '11px', color: labelColor }}>
                          <span>Actual spent</span>
                          <span style={{ fontWeight: '600', color: textColor }}>{formatAmount(actualSpend, false, isMobile, false)}</span>
                        </div>
                        <div style={{ width: '100%', height: isMobile ? '7px' : '8px', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)', borderRadius: '999px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${actualWidth}%`,
                            background: actualWidth < 70 ? '#10b981' : actualWidth < 90 ? '#f59e0b' : '#ef4444',
                            borderRadius: '999px',
                            transition: 'width 0.6s ease'
                          }} />
                        </div>
                      </div>

                      {/* Diff label */}
                      <div style={{ textAlign: 'center', fontSize: isMobile ? '12px' : '13px', fontWeight: '600', color: isUnder ? '#10b981' : '#ef4444' }}>
                        {isUnder
                          ? `You're ${formatAmount(paceDiff, false, isMobile, false)} under pace 🎯`
                          : `You're ${formatAmount(Math.abs(paceDiff), false, isMobile, false)} over pace ⚡`}
                      </div>
                    </>
                  )}

                  {/* ── Divider + Section D: Smart Pulse alerts ── */}
                  {smartAlerts.length > 0 && (
                    <>
                      <div style={dividerStyle} />
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        overflowX: 'auto',
                        paddingBottom: '2px',
                        WebkitOverflowScrolling: 'touch',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                      }}>
                        {smartAlerts.map((alert, i) => (
                          <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: isMobile ? '7px 10px' : '8px 14px',
                            borderRadius: '10px',
                            flexShrink: 0,
                            background: isDark ? `${alert.color}18` : `${alert.color}12`,
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 500,
                            color: isDark ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.72)',
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${alert.color}30`,
                            borderLeft: `3px solid ${alert.color}`
                          }}>
                            <span style={{ fontSize: isMobile ? '13px' : '15px' }}>{alert.emoji}</span>
                            <span>{alert.text}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </ModernFloatingCard>
              );
            })()}

            {/* ── 4. TOP MOVERS VS LAST MONTH ──────────────────────── */}
            {topMovers.length > 0 && (
              <ModernFloatingCard
                style={{ ...styles.chartCard, marginBottom: isMobile ? '12px' : '16px', padding: isMobile ? '16px' : '20px' }}
                themeMode={themeMode}
                isMobile={isMobile}
                glowColor="rgba(139, 92, 246, 0.2)"
              >
                <div style={{ marginBottom: isMobile ? '12px' : '14px' }}>
                  <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: isDark ? '#f8fafc' : '#0f172a', marginBottom: '2px' }}>
                    📊 Top Movers vs Last Month
                  </div>
                  <div style={{ fontSize: isMobile ? '10px' : '12px', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
                    Biggest category shifts
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '8px' : '10px' }}>
                  {topMovers.map((mover, i) => {
                    const isUp = mover.diff > 0;
                    const moverColor = isUp ? '#ef4444' : '#10b981';
                    const arrow = isUp ? '↑' : '↓';
                    const sign = isUp ? '+' : '';
                    return (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: isMobile ? '8px 10px' : '10px 14px',
                        borderRadius: '10px',
                        background: isDark ? `${moverColor}12` : `${moverColor}0a`,
                        border: `1px solid ${moverColor}25`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px' }}>
                          <span style={{
                            fontSize: isMobile ? '14px' : '18px',
                            width: isMobile ? '20px' : '24px',
                            textAlign: 'center',
                            color: moverColor,
                            fontWeight: '700'
                          }}>{arrow}</span>
                          <div>
                            <div style={{ fontSize: isMobile ? '11px' : '13px', fontWeight: '600', color: isDark ? '#f8fafc' : '#0f172a' }}>
                              {mover.name}
                            </div>
                            <div style={{ fontSize: isMobile ? '9px' : '11px', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
                              {formatAmount(mover.prev, false, true, false)} → {formatAmount(mover.curr, false, true, false)}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: moverColor }}>
                            {sign}{formatAmount(mover.diff, false, isMobile, false)}
                          </div>
                          <div style={{ fontSize: isMobile ? '9px' : '11px', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
                            {sign}{mover.pctChange.toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ModernFloatingCard>
            )}

            {/* ── 5. MONTHLY FINANCIAL OVERVIEW — ComposedChart ─────── */}
            <ModernFloatingCard
              style={{ ...styles.chartCard, marginBottom: isMobile ? '12px' : '16px' }}
              themeMode={themeMode}
              isMobile={isMobile}
              glowColor="rgba(34, 197, 94, 0.3)"
            >
              <div style={styles.chartHeader}>
                <div style={styles.chartTitle}>
                  <div style={styles.chartTitleIcon}>
                    <TrendingUp size={isMobile ? 16 : isTablet ? 18 : 20} />
                  </div>
                  <div>
                    <h3 style={styles.chartTitleText}>
                      {isMobile ? t('dashboard.charts.monthlyOverview') : t('dashboard.charts.monthlyFinancialOverview')}
                    </h3>
                    <p style={styles.chartTitleSubtext}>
                      Income vs Expenses · Net Savings Trend
                    </p>
                  </div>
                </div>
              </div>

              {/* Mini legend-stats row — latest month change vs previous */}
              {timeSeriesData.length > 0 && (() => {
                const latest = timeSeriesData[timeSeriesData.length - 1];
                const prev = timeSeriesData.length > 1 ? timeSeriesData[timeSeriesData.length - 2] : null;
                const incomeChange = prev ? ((latest.income - prev.income) / Math.max(prev.income, 1)) * 100 : 0;
                const expenseChange = prev ? ((latest.expenses - prev.expenses) / Math.max(prev.expenses, 1)) * 100 : 0;
                return (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: isMobile ? '12px' : '20px',
                    padding: isMobile ? '8px 12px 0' : '8px 20px 0',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: isMobile ? '10px' : '12px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#22c55e' }} />
                      <span style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}>Income</span>
                      {prev && (
                        <span style={{ color: incomeChange >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                          {incomeChange >= 0 ? '↑' : '↓'}{Math.abs(incomeChange).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: isMobile ? '10px' : '12px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#ef4444' }} />
                      <span style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}>Expenses</span>
                      {prev && (
                        <span style={{ color: expenseChange <= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                          {expenseChange >= 0 ? '↑' : '↓'}{Math.abs(expenseChange).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: isMobile ? '10px' : '12px' }}>
                      <div style={{ width: '12px', height: '3px', borderRadius: '2px', background: '#3b82f6' }} />
                      <span style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}>Net Savings</span>
                    </div>
                  </div>
                );
              })()}

              <div style={styles.chartContent}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={timeSeriesData}
                    margin={{
                      top: 20,
                      right: isMobile ? 10 : 30,
                      left: isMobile ? 10 : 20,
                      bottom: isMobile ? 10 : 20
                    }}
                  >
                    <defs>
                      <linearGradient id="barIncomeGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0.5} />
                      </linearGradient>
                      <linearGradient id="barExpenseGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0.5} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: isMobile ? 9 : 12, fontWeight: 500, fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}
                      axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
                      tickLine={false}
                      interval={isMobile ? 1 : 0}
                    />
                    <YAxis
                      tickFormatter={value => formatAmount(value, false, true, false)}
                      tick={{ fontSize: isMobile ? 9 : 12, fontWeight: 500, fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}
                      axisLine={false}
                      tickLine={false}
                      width={isMobile ? 55 : 70}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        ...chartTooltipStyle,
                        borderRadius: '12px',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                      }}
                      formatter={(value, name) => [formatAmount(value, false, false, false), name]}
                      labelStyle={{ fontWeight: '700', marginBottom: '4px' }}
                      cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                    />
                    {/* Grouped income bar */}
                    <Bar
                      dataKey="income"
                      name={t('dashboard.charts.income')}
                      fill="url(#barIncomeGrad)"
                      radius={[4, 4, 0, 0]}
                      barSize={isMobile ? 14 : 24}
                    />
                    {/* Grouped expenses bar */}
                    <Bar
                      dataKey="expenses"
                      name={t('dashboard.charts.expenses')}
                      fill="url(#barExpenseGrad)"
                      radius={[4, 4, 0, 0]}
                      barSize={isMobile ? 14 : 24}
                    />
                    {/* Net savings dashed line overlay */}
                    <Line
                      type="monotone"
                      dataKey="savings"
                      name={t('dashboard.charts.netSavings')}
                      stroke="#3b82f6"
                      strokeWidth={isMobile ? 2 : 3}
                      strokeDasharray="6 3"
                      dot={{ fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 2, r: isMobile ? 3 : 5 }}
                      activeDot={{ r: isMobile ? 5 : 7, stroke: '#3b82f6', strokeWidth: 2, fill: '#ffffff' }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </ModernFloatingCard>
          </div>
        )}
        
        {activeTab === 1 && (
          <div style={styles.chartContainer}>
            <ModernFloatingCard 
              style={styles.chartCard} 
              themeMode={themeMode}
              isMobile={isMobile}
              glowColor="rgba(147, 51, 234, 0.3)"
            >
              <div style={styles.chartHeader}>
                <div style={styles.chartTitle}>
                  <div style={styles.chartTitleIcon}>
                    <Circle size={isMobile ? 16 : isTablet ? 18 : 20} />
                  </div>
                  <div>
                    <h3 style={styles.chartTitleText}>
                      {isMobile ? t('dashboard.charts.spendingBreakdown') : t('dashboard.charts.categorySpendingDistribution')}
                    </h3>
                    <p style={styles.chartTitleSubtext}>
                      {isMobile ? t('dashboard.thisMonth') : t('dashboard.charts.completeBreakdown')}
                    </p>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '8px' : '12px'
                }}>
                  <div style={{
                    ...styles.chip,
                    background: themeMode === 'light' ? 'rgba(147, 51, 234, 0.1)' : 'rgba(147, 51, 234, 0.2)',
                    borderColor: 'rgba(147, 51, 234, 0.3)',
                    color: '#9333ea',
                    fontSize: isMobile ? '10px' : '12px'
                  }}>
                    {categoryData.length} {t('dashboard.categoriesLabel')}
                  </div>
                  <div style={{
                    ...styles.chip,
                    background: themeMode === 'light' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    color: '#3b82f6',
                    fontSize: isMobile ? '10px' : '12px'
                  }}>
                    {formatAmount(categoryData.reduce((sum, cat) => sum + cat.value, 0), false, isMobile, false)} Total
                  </div>
                </div>
              </div>
              
              <div style={styles.chartContent}>
                {categoryData.length === 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    gap: isMobile ? '16px' : '24px',
                    padding: isMobile ? '32px 16px' : '48px 32px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      width: isMobile ? '64px' : '80px',
                      height: isMobile ? '64px' : '80px',
                      borderRadius: '50%',
                      background: themeMode === 'light' ? 'rgba(147, 51, 234, 0.1)' : 'rgba(147, 51, 234, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(147, 51, 234, 0.3)'
                    }}>
                      <Circle size={isMobile ? 24 : 32} color="#9333ea" />
                    </div>
                    <div>
                      <h4 style={{
                        color: themeMode === 'light' ? '#1f2937' : '#ffffff',
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: '600',
                        margin: '0 0 8px 0'
                      }}>
                        No spending data available
                      </h4>
                      <p style={{
                        color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                        fontSize: isMobile ? '14px' : '16px',
                        margin: 0,
                        maxWidth: '280px'
                      }}>
                        Start tracking your expenses to see spending patterns by category
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '24px' : '32px',
                    height: '100%'
                  }}>
                    {/* Enhanced Pie Chart */}
                    <div style={{
                      flex: isMobile ? 'none' : '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: isMobile ? '280px' : '100%'
                    }}>
                      <ResponsiveContainer width="100%" height={isMobile ? 280 : "100%"}>
                        <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <defs>
                            {categoryData.map((entry, index) => (
                              <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={entry.color} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                              </linearGradient>
                            ))}
                          </defs>
                          <Pie
                            data={categoryData}
                            cx="50%"
                            cy="50%"
                            innerRadius={isMobile ? 60 : isTablet ? 80 : 100}
                            outerRadius={isMobile ? 120 : isTablet ? 140 : 180}
                            paddingAngle={3}
                            dataKey="value"
                            label={false}
                            animationBegin={0}
                            animationDuration={1200}
                            animationEasing="ease-out"
                          >
                            {categoryData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={`url(#gradient-${index})`}
                                stroke={themeMode === 'light' ? '#ffffff' : '#44444e'}
                                strokeWidth={3}
                                style={{ 
                                  filter: 'drop-shadow(0px 6px 12px rgba(0,0,0,0.15))',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }}
                              />
                            ))}
                          </Pie>
                          
                          {/* Center content */}
                          <g>
                            <circle 
                              cx="50%" 
                              cy="50%" 
                              r={isMobile ? 45 : isTablet ? 65 : 85}
                              fill={themeMode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.8)'}
                              stroke={themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'}
                              strokeWidth="2"
                            />
                            <text 
                              x="50%" 
                              y="45%" 
                              textAnchor="middle" 
                              dominantBaseline="middle"
                              style={{
                                fontSize: isMobile ? '11px' : isTablet ? '13px' : '15px',
                                fontWeight: '600',
                                fill: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)'
                              }}
                            >
                              {t('dashboard.charts.totalSpent')}
                            </text>
                            <text 
                              x="50%" 
                              y="55%" 
                              textAnchor="middle" 
                              dominantBaseline="middle"
                              style={{
                                fontSize: isMobile ? '14px' : isTablet ? '16px' : '20px',
                                fontWeight: '700',
                                fill: themeMode === 'light' ? '#1f2937' : '#ffffff'
                              }}
                            >
                              {formatAmount(categoryData.reduce((sum, cat) => sum + cat.value, 0), false, true, false)}
                            </text>
                          </g>
                          
                          <RechartsTooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div style={{
                                    background: themeMode === 'light' 
                                      ? 'rgba(255, 255, 255, 0.98)' 
                                      : 'rgba(0, 0, 0, 0.98)',
                                    border: `1px solid ${themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
                                    borderRadius: '12px',
                                    padding: '16px',
                                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    minWidth: '200px'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px',
                                      marginBottom: '12px'
                                    }}>
                                      <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        background: data.color
                                      }} />
                                      <span style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: themeMode === 'light' ? '#1f2937' : '#ffffff'
                                      }}>
                                        {data.name}
                                      </span>
                                    </div>
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 1fr',
                                      gap: '12px',
                                      fontSize: '14px'
                                    }}>
                                      <div>
                                        <div style={{
                                          color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                          marginBottom: '4px'
                                        }}>
                                          Amount
                                        </div>
                                        <div style={{
                                          fontWeight: '600',
                                          color: themeMode === 'light' ? '#1f2937' : '#ffffff'
                                        }}>
                                          {formatAmount(data.value)}
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{
                                          color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                          marginBottom: '4px'
                                        }}>
                                          Percentage
                                        </div>
                                        <div style={{
                                          fontWeight: '600',
                                          color: themeMode === 'light' ? '#1f2937' : '#ffffff'
                                        }}>
                                          {data.percentage.toFixed(1)}%
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{
                                          color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                          marginBottom: '4px'
                                        }}>
                                          Transactions
                                        </div>
                                        <div style={{
                                          fontWeight: '600',
                                          color: themeMode === 'light' ? '#1f2937' : '#ffffff'
                                        }}>
                                          {data.transactionCount}
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{
                                          color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                          marginBottom: '4px'
                                        }}>
                                          Average
                                        </div>
                                        <div style={{
                                          fontWeight: '600',
                                          color: themeMode === 'light' ? '#1f2937' : '#ffffff'
                                        }}>
                                          {formatAmount(data.value / data.transactionCount)}
                                        </div>
                                      </div>
                                    </div>
                                    {data.budget > 0 && (
                                      <div style={{
                                        marginTop: '12px',
                                        paddingTop: '12px',
                                        borderTop: `1px solid ${themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`
                                      }}>
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          marginBottom: '8px'
                                        }}>
                                          <span style={{
                                            color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                            fontSize: '13px'
                                          }}>
                                            Budget Usage
                                          </span>
                                          <span style={{
                                            fontWeight: '600',
                                            fontSize: '13px',
                                            color: data.budgetUtilization > 100 ? '#ef4444' : 
                                                  data.budgetUtilization > 80 ? '#f59e0b' : '#22c55e'
                                          }}>
                                            {data.budgetUtilization.toFixed(1)}%
                                          </span>
                                        </div>
                                        <div style={{
                                          height: '6px',
                                          background: themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                                          borderRadius: '3px',
                                          overflow: 'hidden'
                                        }}>
                                          <div style={{
                                            height: '100%',
                                            width: `${Math.min(data.budgetUtilization, 100)}%`,
                                            background: data.budgetUtilization > 100 ? '#ef4444' : 
                                                      data.budgetUtilization > 80 ? '#f59e0b' : '#22c55e',
                                            borderRadius: '3px',
                                            transition: 'width 0.5s ease'
                                          }} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Enhanced Legend with Interactive Elements */}
                    <div style={{
                      flex: isMobile ? 'none' : '0 0 300px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: isMobile ? '12px' : '8px',
                      maxHeight: isMobile ? 'none' : '100%',
                      overflowY: isMobile ? 'visible' : 'auto',
                      paddingRight: isMobile ? '0' : '8px'
                    }}>
                      <div style={{
                        marginBottom: isMobile ? '8px' : '16px'
                      }}>
                        <h4 style={{
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: '600',
                          margin: '0 0 8px 0',
                          color: themeMode === 'light' ? '#1f2937' : '#ffffff'
                        }}>
                          Category Breakdown
                        </h4>
                        <p style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                          margin: 0
                        }}>
                          {categoryData.length} {t('dashboard.categoriesCount')} • {categoryData.reduce((sum, cat) => sum + cat.transactionCount, 0)} {t('dashboard.transactionsCount')}
                        </p>
                      </div>
                      
                      {categoryData.map((entry, index) => (
                        <div 
                          key={`legend-${index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: isMobile ? '12px' : '16px',
                            background: themeMode === 'light' 
                              ? 'rgba(255, 255, 255, 0.8)' 
                              : 'rgba(255, 255, 255, 0.05)',
                            border: `1px solid ${themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`,
                            borderRadius: isMobile ? '12px' : '16px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            backdropFilter: 'blur(10px)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: '1',
                            minWidth: '0'
                          }}>
                            <div style={{
                              width: isMobile ? '12px' : '16px',
                              height: isMobile ? '12px' : '16px',
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${entry.color}, ${entry.color}dd)`,
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              flexShrink: 0,
                              boxShadow: `0 2px 8px ${entry.color}40`
                            }} />
                            <div style={{
                              flex: '1',
                              minWidth: '0'
                            }}>
                              <div style={{
                                fontSize: isMobile ? '13px' : '14px',
                                fontWeight: '600',
                                color: themeMode === 'light' ? '#1f2937' : '#ffffff',
                                marginBottom: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {entry.name}
                              </div>
                              <div style={{
                                fontSize: isMobile ? '11px' : '12px',
                                color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span>{entry.transactionCount} transaction{entry.transactionCount !== 1 ? 's' : ''}</span>
                                {entry.budget > 0 && (
                                  <span style={{
                                    color: entry.budgetUtilization > 100 ? '#ef4444' : 
                                          entry.budgetUtilization > 80 ? '#f59e0b' : '#22c55e',
                                    fontWeight: '500'
                                  }}>
                                    {entry.budgetUtilization.toFixed(0)}% budget
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div style={{
                            textAlign: 'right',
                            flexShrink: 0
                          }}>
                            <div style={{
                              fontSize: isMobile ? '13px' : '14px',
                              fontWeight: '700',
                              color: themeMode === 'light' ? '#1f2937' : '#ffffff',
                              marginBottom: '2px'
                            }}>
                              {formatAmount(entry.value, false, isMobile, false)}
                            </div>
                            <div style={{
                              fontSize: isMobile ? '11px' : '12px',
                              color: entry.color,
                              fontWeight: '600'
                            }}>
                              {entry.percentage.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ModernFloatingCard>
            
            {/* Category Treemap */}
            {categoryData.length > 0 && (
              <ModernFloatingCard 
                style={{ ...styles.chartCard, marginTop: isMobile ? '12px' : '16px' }} 
                themeMode={themeMode}
                isMobile={isMobile}
                glowColor="rgba(147, 51, 234, 0.3)"
              >
                <div style={styles.chartHeader}>
                  <div style={styles.chartTitle}>
                    <div style={styles.chartTitleIcon}>
                      <Layers size={isMobile ? 16 : isTablet ? 18 : 20} />
                    </div>
                    <div>
                      <h3 style={styles.chartTitleText}>
                        {isMobile ? t('dashboard.charts.categoryTreemap') : t('dashboard.charts.categorySpendingTreemap')}
                      </h3>
                      <p style={styles.chartTitleSubtext}>
                        Size = Amount • Color = Budget status
                      </p>
                    </div>
                  </div>
                </div>
                <div style={{ height: isMobile ? 300 : 400, padding: isMobile ? '12px' : '20px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                      data={categoryData}
                      dataKey="value"
                      aspectRatio={4 / 3}
                      stroke={themeMode === 'light' ? '#ffffff' : '#44444e'}
                      fill="#8884d8"
                      content={(props) => {
                        const { x, y, width, height, name, value, budgetUtilization } = props;
                        if (width < 40 || height < 30) return null;
                        
                        const bgColor = budgetUtilization <= 70 
                          ? '#10b981' 
                          : budgetUtilization <= 90 
                          ? '#f59e0b' 
                          : budgetUtilization <= 100 
                          ? '#f97316' 
                          : '#ef4444';
                        
                        return (
                          <g>
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              style={{
                                fill: bgColor,
                                stroke: themeMode === 'light' ? '#ffffff' : '#44444e',
                                strokeWidth: 2,
                                opacity: 0.9
                              }}
                            />
                            {width > 60 && height > 40 && (
                              <>
                                <text
                                  x={x + width / 2}
                                  y={y + height / 2 - 10}
                                  textAnchor="middle"
                                  fill="#ffffff"
                                  fontSize={Math.min(width / 8, 14)}
                                  fontWeight="700"
                                >
                                  {name}
                                </text>
                                <text
                                  x={x + width / 2}
                                  y={y + height / 2 + 10}
                                  textAnchor="middle"
                                  fill="#ffffff"
                                  fontSize={Math.min(width / 10, 12)}
                                  fontWeight="600"
                                >
                                  {formatAmount(value, false, isMobile, false)}
                                </text>
                              </>
                            )}
                          </g>
                        );
                      }}
                    />
                  </ResponsiveContainer>
                </div>
                <div style={{ 
                  padding: isMobile ? '8px 16px' : '12px 24px',
                  display: 'flex',
                  gap: isMobile ? '8px' : '12px',
                  flexWrap: 'wrap',
                  fontSize: isMobile ? '10px' : '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', background: '#10b981', borderRadius: '4px' }} />
                    <span>{t('dashboard.charts.underBudget')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', background: '#f59e0b', borderRadius: '4px' }} />
                    <span>{t('dashboard.charts.nearLimitRange')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', background: '#f97316', borderRadius: '4px' }} />
                    <span>{t('dashboard.charts.atLimit')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', background: '#ef4444', borderRadius: '4px' }} />
                    <span>{t('dashboard.charts.overBudgetRange')}</span>
                  </div>
                </div>
              </ModernFloatingCard>
            )}

            {/* Category Budget Progress Bars */}
            {categoryData.length > 0 && categoryData.some(c => c.budget > 0) && (
              <ModernFloatingCard 
                style={{ ...styles.chartCard, marginTop: isMobile ? '12px' : '16px' }} 
                themeMode={themeMode}
                isMobile={isMobile}
                glowColor="rgba(102, 126, 234, 0.3)"
              >
                <div style={styles.chartHeader}>
                  <div style={styles.chartTitle}>
                    <div style={styles.chartTitleIcon}>
                      <BarChart2 size={isMobile ? 16 : isTablet ? 18 : 20} />
                    </div>
                    <div>
                      <h3 style={styles.chartTitleText}>
                        Category Budget Progress
                      </h3>
                      <p style={styles.chartTitleSubtext}>
                        Detailed budget utilization by category
                      </p>
                    </div>
                  </div>
                </div>
                <div style={{ padding: isMobile ? '12px' : '20px' }}>
                  {categoryData.filter(c => c.budget > 0).sort((a, b) => b.budgetUtilization - a.budgetUtilization).map((cat, idx) => (
                    <div key={idx} style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginBottom: '8px',
                        fontSize: isMobile ? '13px' : '15px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                          <span>{cat.icon || '📊'}</span>
                          <span>{cat.name}</span>
                          {cat.budgetUtilization > 100 && (
                            <span style={{
                              padding: '2px 6px',
                              background: '#ef4444',
                              color: '#ffffff',
                              fontSize: isMobile ? '9px' : '10px',
                              borderRadius: '4px',
                              fontWeight: '700'
                            }}>
                              OVER
                            </span>
                          )}
                        </div>
                        <div>
                          <span style={{ fontWeight: '700' }}>{formatAmount(cat.value, false, isMobile, false)}</span>
                          <span style={{ 
                            color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                            marginLeft: '4px',
                            fontSize: isMobile ? '11px' : '13px'
                          }}>
                            / {formatAmount(cat.budget, false, isMobile, false)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div style={{ position: 'relative' }}>
                        <div style={{ 
                          width: '100%', 
                          height: isMobile ? '24px' : '28px', 
                          background: themeMode === 'light' ? '#e5e7eb' : '#374151',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <div style={{
                            width: `${Math.min(cat.budgetUtilization, 100)}%`,
                            height: '100%',
                            background: cat.budgetUtilization <= 70 
                              ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                              : cat.budgetUtilization <= 90 
                              ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                              : cat.budgetUtilization <= 100 
                              ? 'linear-gradient(90deg, #f97316 0%, #ea580c 100%)'
                              : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                            transition: 'width 0.5s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            paddingRight: '8px'
                          }}>
                            <span style={{ 
                              color: '#ffffff', 
                              fontSize: isMobile ? '11px' : '13px',
                              fontWeight: '700'
                            }}>
                              {cat.budgetUtilization.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        
                        {/* Overflow indicator if over 100% */}
                        {cat.budgetUtilization > 100 && (
                          <div style={{
                            position: 'absolute',
                            right: `-${(cat.budgetUtilization - 100) / cat.budgetUtilization * 100}%`,
                            top: 0,
                            height: '100%',
                            width: `${(cat.budgetUtilization - 100)}%`,
                            background: 'repeating-linear-gradient(45deg, #ef4444, #ef4444 10px, #dc2626 10px, #dc2626 20px)',
                            opacity: 0.5,
                            borderRadius: '0 8px 8px 0'
                          }} />
                        )}
                      </div>
                      
                      {/* Details */}
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: isMobile ? '11px' : '12px',
                        color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                        marginTop: '6px'
                      }}>
                        <span>
                          Remaining: {formatAmount(Math.max(0, cat.budget - cat.value), false, isMobile, false)}
                        </span>
                        <span>
                          {cat.transactionCount} transaction{cat.transactionCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </ModernFloatingCard>
            )}
          </div>
        )}
        
        {activeTab === 2 && (
          <div style={styles.chartContainer}>
            <ModernFloatingCard 
              style={styles.chartCard} 
              themeMode={themeMode}
              isMobile={isMobile}
              glowColor="rgba(59, 130, 246, 0.3)"
            >
              <div style={styles.chartHeader}>
                <div style={styles.chartTitle}>
                  <div style={styles.chartTitleIcon}>
                    <Activity size={isMobile ? 16 : isTablet ? 18 : 20} />
                  </div>
                  <div>
                    <h3 style={styles.chartTitleText}>
                      {isMobile ? t('dashboard.charts.trends') : t('dashboard.charts.financialTrendsAnalysis')}
                    </h3>
                    <p style={styles.chartTitleSubtext}>
                      {isMobile ? t('dashboard.charts.savingsRate') : t('dashboard.charts.trackFinancialJourney')}
                    </p>
                  </div>
                </div>
              </div>
              
              <div style={styles.chartContent}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={timeSeriesData} 
                    margin={{ 
                      top: 20, 
                      right: isMobile ? 10 : 30, 
                      left: isMobile ? 10 : 20, 
                      bottom: isMobile ? 10 : 20 
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ 
                        fontSize: isMobile ? 10 : isTablet ? 12 : 14, 
                        fontWeight: 500, 
                        fill: themeMode === 'light' ? '#1f2937' : '#ffffff' 
                      }}
                      axisLine={{ stroke: themeMode === 'light' ? '#e5e7eb' : '#374151', strokeWidth: 1 }}
                      tickLine={{ stroke: themeMode === 'light' ? '#e5e7eb' : '#374151', strokeWidth: 1 }}
                      interval={isMobile ? 1 : 0}
                    />
                    <YAxis 
                      tick={{ 
                        fontSize: isMobile ? 10 : isTablet ? 12 : 14, 
                        fontWeight: 500, 
                        fill: themeMode === 'light' ? '#1f2937' : '#ffffff' 
                      }}
                      axisLine={{ stroke: themeMode === 'light' ? '#e5e7eb' : '#374151', strokeWidth: 1 }}
                      tickLine={{ stroke: themeMode === 'light' ? '#e5e7eb' : '#374151', strokeWidth: 1 }}
                      width={isMobile ? 50 : 80}
                    />
                    <RechartsTooltip contentStyle={chartTooltipStyle} />
                    <Legend 
                      wrapperStyle={{ 
                        fontSize: isMobile ? '10px' : isTablet ? '12px' : '15px', 
                        fontWeight: 600, 
                        paddingTop: '20px' 
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="savingsRate" 
                      stroke="#3b82f6"
                      strokeWidth={isMobile ? 3 : 4}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: isMobile ? 4 : 6 }}
                      activeDot={{ r: isMobile ? 6 : 8, stroke: '#3b82f6', strokeWidth: 3 }}
                      name={t('dashboard.charts.savingsRatePercent')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={() => 20} 
                      stroke="#22c55e"
                      strokeWidth={isMobile ? 1 : 2}
                      strokeDasharray="5 5"
                      dot={false}
                      name={t('dashboard.charts.goodSavingsLine')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={() => 10} 
                      stroke="#f59e0b"
                      strokeWidth={isMobile ? 1 : 2}
                      strokeDasharray="5 5"
                      dot={false}
                      name={t('dashboard.charts.minimumSavingsLine')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ModernFloatingCard>
            
            {/* Month-over-Month Growth */}
            {timeSeriesData.length >= 2 && (
              <ModernFloatingCard 
                style={{ ...styles.chartCard, marginTop: isMobile ? '12px' : '16px' }} 
                themeMode={themeMode}
                isMobile={isMobile}
                glowColor="rgba(34, 197, 94, 0.3)"
              >
                <div style={styles.chartHeader}>
                  <div style={styles.chartTitle}>
                    <div style={styles.chartTitleIcon}>
                      <GitCompare size={isMobile ? 16 : isTablet ? 18 : 20} />
                    </div>
                    <div>
                      <h3 style={styles.chartTitleText}>
                        Month-over-Month Growth
                      </h3>
                      <p style={styles.chartTitleSubtext}>
                        Expense changes compared to previous month
                      </p>
                    </div>
                  </div>
                </div>
                <div style={styles.chartContent}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={timeSeriesData.map((month, idx) => ({
                        ...month,
                        change: idx > 0 ? month.expenses - timeSeriesData[idx - 1].expenses : 0,
                        changePercent: idx > 0 && timeSeriesData[idx - 1].expenses > 0 
                          ? ((month.expenses - timeSeriesData[idx - 1].expenses) / timeSeriesData[idx - 1].expenses) * 100 
                          : 0
                      }))}
                      margin={{ 
                        top: 20, 
                        right: isMobile ? 10 : 30, 
                        left: isMobile ? 10 : 20, 
                        bottom: isMobile ? 10 : 20 
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: isMobile ? 10 : 14, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
                        interval={isMobile ? 1 : 0}
                      />
                      <YAxis 
                        tickFormatter={(val) => `${val > 0 ? '+' : ''}${val.toFixed(0)}%`}
                        tick={{ fontSize: isMobile ? 10 : 14, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
                      />
                      <RechartsTooltip 
                        contentStyle={chartTooltipStyle}
                        formatter={(val, name) => {
                          if (name === 'changePercent') return `${val > 0 ? '+' : ''}${val.toFixed(1)}%`;
                          return formatAmount(val, false, isMobile, false);
                        }}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Bar dataKey="changePercent" name="% Change" radius={[8, 8, 0, 0]}>
                        {timeSeriesData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={index === 0 ? '#6b7280' : entry.expenses > timeSeriesData[index - 1]?.expenses ? '#ef4444' : '#10b981'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ModernFloatingCard>
            )}

            {/* Top Categories Trend Lines - Real Historical Data */}
            {categoryData.length > 0 && categoryTrendData.length >= 2 && (
              <ModernFloatingCard 
                style={{ ...styles.chartCard, marginTop: isMobile ? '12px' : '16px' }} 
                themeMode={themeMode}
                isMobile={isMobile}
                glowColor="rgba(147, 51, 234, 0.3)"
              >
                <div style={styles.chartHeader}>
                  <div style={styles.chartTitle}>
                    <div style={styles.chartTitleIcon}>
                      <Layers size={isMobile ? 16 : isTablet ? 18 : 20} />
                    </div>
                    <div>
                      <h3 style={styles.chartTitleText}>
                        Top 5 Category Trends
                      </h3>
                      <p style={styles.chartTitleSubtext}>
                        Spending evolution by category over time
                      </p>
                    </div>
                  </div>
                </div>
                <div style={styles.chartContent}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart 
                      data={categoryTrendData}
                      margin={{ 
                        top: 20, 
                        right: isMobile ? 10 : 30, 
                        left: isMobile ? 10 : 20, 
                        bottom: isMobile ? 10 : 20 
                      }}
                    >
                      <defs>
                        {categoryData.slice(0, 5).map((cat, idx) => (
                          <linearGradient key={`gradient-${cat.name}`} id={`gradient-${cat.name}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={cat.color} stopOpacity={0.8}/>
                            <stop offset="95%" stopColor={cat.color} stopOpacity={0.1}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: isMobile ? 10 : 14, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
                        interval={isMobile ? 1 : 0}
                      />
                      <YAxis 
                        tickFormatter={(val) => formatAmount(val, false, isMobile, false)}
                        tick={{ fontSize: isMobile ? 10 : 14, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
                      />
                      <RechartsTooltip 
                        contentStyle={chartTooltipStyle}
                        formatter={(val) => formatAmount(val, false, isMobile, false)}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 600 }} 
                      />
                      {categoryData.slice(0, 5).map((cat, idx) => (
                        <Area
                          key={cat.name}
                          type="monotone"
                          dataKey={cat.name}
                          stackId="1"
                          stroke={cat.color}
                          fill={`url(#gradient-${cat.name})`}
                          strokeWidth={isMobile ? 2 : 2.5}
                          name={cat.name}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Category Changes Summary */}
                {categoryChanges.length > 0 && (
                  <div style={{ 
                    padding: isMobile ? '12px 16px' : '16px 24px',
                    background: themeMode === 'light' ? 'rgba(147, 51, 234, 0.1)' : 'rgba(147, 51, 234, 0.2)',
                    borderRadius: '0 0 16px 16px'
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: '600',
                      color: themeMode === 'light' ? '#1f2937' : '#ffffff',
                      marginBottom: isMobile ? '8px' : '10px'
                    }}>
                      Month-over-Month Changes
                    </div>
                    <div style={{ 
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: isMobile ? '6px' : '8px'
                    }}>
                      {categoryChanges.map(change => (
                        <div 
                          key={change.name}
                          style={{
                            padding: isMobile ? '6px 10px' : '8px 12px',
                            background: change.direction === 'up' 
                              ? (themeMode === 'light' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.25)')
                              : change.direction === 'down'
                              ? (themeMode === 'light' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.25)')
                              : (themeMode === 'light' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.25)'),
                            borderRadius: '8px',
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: '600',
                            color: themeMode === 'light' ? '#1f2937' : '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <span>{categoryData.find(c => c.name === change.name)?.icon}</span>
                          <span>{change.name}</span>
                          <span>
                            {change.direction === 'up' ? '↑' : change.direction === 'down' ? '↓' : '→'}
                            {Math.abs(change.change).toFixed(0)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ModernFloatingCard>
            )}
          </div>
        )}
        
        {activeTab === 3 && (
          <div style={styles.chartContainer}>
            <AdvancedAnalytics
              transactions={transactions}
              categories={categories}
              currentBalance={enhancedMetrics.netWorth || 0}
              totalIncome={enhancedMetrics.totalIncome}
              totalExpenses={enhancedMetrics.totalExpenses}
              monthlyBudget={enhancedMetrics.totalBudget}
              categoryData={categoryData}
              themeMode={themeMode}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCharts;